import type { ChatMessage, StreamEvent } from '@hypevault/shared-types';
import { AlertBoxWidget } from './AlertBoxWidget';
import { StaticTextWidget } from './StaticTextWidget';
import { StaticImageWidget } from './StaticImageWidget';
import { ActivityTickerWidget } from './ActivityTickerWidget';
import { LatestEventsWidget } from './LatestEventsWidget';
import { GoalWidget } from './GoalWidget';
import { CustomWidget } from './CustomWidget';
import { PlaceholderWidget } from './PlaceholderWidget';
import { ShoutoutWidget } from './ShoutoutWidget';
import { EmoteWallWidget } from './EmoteWallWidget';
import { ViewerCountWidget } from './ViewerCountWidget';
import { MultiChatWidget } from './MultiChatWidget';
import { MultiChatHorizontalWidget } from './MultiChatHorizontalWidget';
import { ChatHighlightWidget } from './ChatHighlightWidget';
import { CountdownWidget } from './CountdownWidget';
import { StreamStartingWidget } from './StreamStartingWidget';
import { SupportersWidget } from './SupportersWidget';
import { LeaderboardWidget } from './LeaderboardWidget';
import { TimerWidget } from './TimerWidget';
import { AdBreakWidget } from './AdBreakWidget';
import { MusicWidget } from './MusicWidget';
import { RedemptionsQueueWidget } from './RedemptionsQueueWidget';
import { StreamScheduleWidget } from './StreamScheduleWidget';
import { PollWidget } from './PollWidget';
import { CardTimerWidget } from './CardTimerWidget';
import { CardAlertWidget } from './CardAlertWidget';
import { FullscreenAlertsWidget } from './FullscreenAlertsWidget';
import { MarathonCompactWidget } from './MarathonCompactWidget';
import { MarathonMinimalWidget } from './MarathonMinimalWidget';
import { MarathonFullWidget } from './MarathonFullWidget';
import { GameWidget } from './GameWidget';
import { Watermark } from './Watermark';

// Widget types that show the free-tier HypeVault watermark. Skipped:
//   - alert-box, card-alert: transient pop-ups, watermark would flash in/out
//   - static-text, static-image: streamer-supplied content, not HV-branded
//   - custom-widget: paid-tier only
//   - viewer-count: small chip widget where the watermark would dwarf the content
//   - emote-wall, chat-highlight, jumpscare, ad-break, card-timer, countdown:
//     either transient or visually noisy already
//   - gem_leaderboard, hype_meter, image_rotator, magic_pool_ball, slots,
//     chaos_scramble, emoji_decoder, hype_million, draw_guess, game:
//     bypass WidgetView entirely (special-cased in
//     WidgetApp.tsx — see the architectural note there for per-widget
//     rationale). Adding them here would NOT cause the watermark to render;
//     the wrap logic below only fires for widgets that route through
//     WidgetView. Listed for future reference only.
const WATERMARKED_WIDGETS: ReadonlySet<string> = new Set([
  'multi-chat',
  'multi-chat-horizontal',
  'music',
  'song-requests',
  'redemptions-queue',
  'redemptions',
  'stream-schedule',
  'poll',
  'supporters',
  'leaderboard',
  'timer',
  // Widgets that render their own watermark internally so they can anchor it
  // to a specific element (e.g. goals anchors to the last bar, game anchors
  // inside .gx-card):
  //   'game'
  //   'goals'
]);

interface Transform {
  x: number;
  y: number;
  w: number;
  h: number;
  rotation: number;
  opacity: number;
  zIndex: number;
  locked: boolean;
  hidden: boolean;
}

interface WidgetInstance {
  id: string;
  widgetType: string;
  transform: Transform;
  fields: Record<string, unknown>;
}

interface GameStateEvent {
  gameType: string;
  state: unknown;
  config: unknown;
  isActive: boolean;
}

interface MarathonReveal {
  id: string;
  eventType: string;
  contributorName: string;
  secondsAdded: number;
  createdAt: string;
}

export function WidgetView({
  widget,
  latestEvent,
  latestChatMessage,
  token,
  gameStateEvent,
  streamerTier,
  latestMarathonReveal,
}: {
  widget: WidgetInstance;
  latestEvent: StreamEvent | null;
  latestChatMessage: ChatMessage | null;
  token: string;
  gameStateEvent: GameStateEvent | null;
  streamerTier?: 'free' | 'paid';
  latestMarathonReveal?: MarathonReveal | null;
}) {
  const node = renderWidget(
    widget,
    latestEvent,
    latestChatMessage,
    token,
    gameStateEvent,
    streamerTier,
    latestMarathonReveal ?? null,
  );
  if (streamerTier === 'free' && WATERMARKED_WIDGETS.has(widget.widgetType)) {
    // Layout-aware watermark: widget renders in the flex-1 area and the
    // watermark drops into its own right-aligned footer row below. Avoids
    // overlapping the bottom-most content of list/bar widgets while keeping
    // the watermark anchored to the visible widget chrome rather than the
    // OBS viewport corner.
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        <div style={{ flex: 1, minHeight: 0 }}>{node}</div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Watermark variant="inline" />
        </div>
      </div>
    );
  }
  return node;
}

function renderWidget(
  widget: WidgetInstance,
  latestEvent: StreamEvent | null,
  latestChatMessage: ChatMessage | null,
  token: string,
  gameStateEvent: GameStateEvent | null,
  streamerTier?: 'free' | 'paid',
  latestMarathonReveal: MarathonReveal | null = null,
) {
  switch (widget.widgetType) {
    case 'alert-box':
      return <AlertBoxWidget fields={widget.fields} latestEvent={latestEvent} />;
    case 'card-alert':
      return <CardAlertWidget fields={widget.fields} latestEvent={latestEvent} />;
    case 'fullscreen-alerts':
      return <FullscreenAlertsWidget fields={widget.fields} latestEvent={latestEvent} />;
    case 'static-text':
      return <StaticTextWidget fields={widget.fields} />;
    case 'static-image':
      return <StaticImageWidget fields={widget.fields} />;
    case 'activity-ticker':
      return (
        <ActivityTickerWidget
          fields={widget.fields}
          latestEvent={latestEvent}
          streamerTier={streamerTier ?? 'free'}
        />
      );
    case 'latest-events':
      return (
        <LatestEventsWidget
          fields={widget.fields}
          latestEvent={latestEvent}
          streamerTier={streamerTier ?? 'free'}
        />
      );
    case 'goals':
      return (
        <GoalWidget
          fields={widget.fields}
          latestEvent={latestEvent}
          streamerTier={streamerTier ?? 'free'}
        />
      );
    case 'marathon-compact':
      return (
        <MarathonCompactWidget
          fields={widget.fields}
          token={token}
          latestEvent={latestEvent}
          testReveal={latestMarathonReveal}
        />
      );
    case 'marathon-minimal':
      return (
        <MarathonMinimalWidget
          fields={widget.fields}
          token={token}
          latestEvent={latestEvent}
          testReveal={latestMarathonReveal}
        />
      );
    case 'marathon-full':
      return (
        <MarathonFullWidget
          fields={widget.fields}
          token={token}
          latestEvent={latestEvent}
          testReveal={latestMarathonReveal}
        />
      );
    case 'shoutout':
      return <ShoutoutWidget fields={widget.fields} latestEvent={latestEvent} />;
    case 'emote-wall':
      return <EmoteWallWidget fields={widget.fields} latestChatMessage={latestChatMessage} />;
    case 'viewer-count':
      return <ViewerCountWidget fields={widget.fields} />;
    case 'multi-chat':
      return (
        <MultiChatWidget
          fields={widget.fields}
          latestChatMessage={latestChatMessage}
          latestEvent={latestEvent}
        />
      );
    case 'multi-chat-horizontal':
      return (
        <MultiChatHorizontalWidget fields={widget.fields} latestChatMessage={latestChatMessage} />
      );
    case 'chat-highlight':
      return <ChatHighlightWidget fields={widget.fields} latestChatMessage={latestChatMessage} />;
    case 'countdown':
      return <CountdownWidget fields={widget.fields} />;
    case 'stream-starting':
      return <StreamStartingWidget fields={widget.fields} />;
    case 'supporters':
      return <SupportersWidget fields={widget.fields} latestEvent={latestEvent} />;
    case 'leaderboard':
      return <LeaderboardWidget fields={widget.fields} latestEvent={latestEvent} />;
    case 'timer':
      return <TimerWidget fields={widget.fields} latestEvent={latestEvent} />;
    case 'ad-break':
      return <AdBreakWidget fields={widget.fields} />;
    case 'music':
    case 'song-requests':
      return <MusicWidget fields={widget.fields} />;
    case 'redemptions-queue':
    case 'redemptions':
      return <RedemptionsQueueWidget fields={widget.fields} latestEvent={latestEvent} />;
    case 'stream-schedule':
      return <StreamScheduleWidget fields={widget.fields} />;
    case 'poll':
      return <PollWidget fields={widget.fields} latestChatMessage={latestChatMessage} />;
    case 'card-timer':
      return <CardTimerWidget fields={widget.fields} />;
    case 'custom-widget':
      return <CustomWidget fields={widget.fields} />;
    case 'game':
      return (
        <GameWidget
          token={token}
          gameStateEvent={gameStateEvent}
          streamerTier={streamerTier ?? 'free'}
        />
      );
    default:
      return <PlaceholderWidget label={widget.widgetType} />;
  }
}
